import * as React from 'react';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { ConfigurationModel, ConfigurationDefaultModel, MinMaxModel, WITH_WITHOUT, ApronBackSplashDefaultModel, ApronBackSplashModel, PlateElement, WashBasinDimension, PlateBuilder } from 'franke-trough-dependencies';
import { GlobalPrivateState, formatMessage, PlateBuilderState,  } from 'src/api';
import { RequestApiResult, requestAPIActions } from 'loopback-react-request';
import Slider from 'rc-slider';
import { DebounceInput } from 'react-debounce-input';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfo, faSpaceShuttle } from '@fortawesome/free-solid-svg-icons';
import { UncontrolledTooltip } from 'reactstrap';
import { FormattedMessage } from 'react-intl';
import MESSAGES from './ApronBacksplash.messages';
import { isConfigurationEditable } from 'src/redux/utils.redux';
import { includes as _includes } from 'lodash';
import { WarningsTooltip } from '../../Warnings';
import PlateDimensionsRequired from '../BasinDimensions/PlateDimensionsRequired';
import PlateDimensionsChoice from '../BasinDimensions/PlateDimensionsChoice';
import ConsolesOverlapChoice from '../Assemblies/ConsoleOverlapChoice';

export interface ApronProps {
  plateBuilder: GlobalPrivateState['plateBuilder'];
  configuration: RequestApiResult<ConfigurationModel>;
  dispatch: any;
}

export interface ApronState {
  apronHeight?: ApronBackSplashModel['apronHeight'];
  heightError: boolean;
}

export const simulatedAprons = (plateBuilder: PlateBuilder | PlateBuilderState) => {
  const plateWithApron = plateBuilder.populateWithConfOverrides({apronBackSplash: {apron: 'with', apronHeight: 150}});
  const leftApron = plateWithApron.elements.find(e => e.type === 'apron' && e.isFirst);
  const rightApron = plateWithApron.elements.find(e => e.type === 'apron' && e.isLast);
 
  return {
    left: leftApron,
    right: rightApron
  };
}

export const apronSpaceAvailable = (plateBuilder: PlateBuilder | PlateBuilderState) => {
  // const apron = plateBuilder.elementsBuilder.buildDepthApron({isFirst: true}, 150);
  // // Manually passing in the positions for the apron... otherwise it starts trying to generate them, and the spacePosition stuff is recently customised for cut-outs
  // const apronSpaces = plateBuilder.calc.spaceSizes(apron, [apron.outerWidth/2, plateBuilder.components.plate.scale.x - apron.outerWidth/2]);
  // // Issue with manually passing positions is that we dont get the space available correctly...
  // console.log('apronSpaceAvailable get');
  const apron = plateBuilder.elementsBuilder.buildDepthApron({}, 150);
  const apronPositions = plateBuilder.calc.spacePositions(apron, null, true);
  const apronSpaces = plateBuilder.calc.spaceSizes(apron);
  // Have to update spaceSizes to be more apron aware
  // console.log('apronSpaceAvailable received', apronPositions, apronSpaces);


  const spaces = {
    left: apronSpaces[0],
    right: apronSpaces[apronSpaces.length - 1]
  };

  if (spaces.left < 0) {
    spaces.left = 0;
  }

  if (spaces.right < 0) {
    spaces.right = 0;
  }

  return spaces;
}

// Apron is needed for other things, and sometimes we need dimensions to increase to also add apron... such a pain but they want tooltips for everything
export const hasSpaceForApron = (aprons: any, spaces: any) => {
  const apronSizeLeft = aprons.left && aprons.left.outerWidth || 0;
  const apronSizeRight = aprons.right && aprons.right.outerWidth || 0;

  const hasSpace = spaces.left >= apronSizeLeft && spaces.right >= apronSizeRight;

  // console.log('hasSpaceForApron', spaces.left, apronSizeLeft, spaces.right, apronSizeRight);

  return hasSpace;
}

export const requiredWidthForApron = (aprons: any, spaces: any, currentWidth: number | null | undefined): number => {
  if (!currentWidth) { return 0; }
  const apronSizeLeft = aprons.left && (aprons.left.outerWidth - spaces.left) || 0;
  const apronSizeRight = aprons.right && (aprons.right.outerWidth - spaces.right) || 0;

  // console.log('requiredWidthForApron', aprons, spaces, apronSizeLeft, apronSizeRight);

  // Since we are looking at the space available, and then removing the apron width, we could get a huge negative number... we really only want to increase the width, so we limit this to be no lower than 0
  const apronSizeLeftLimited = apronSizeLeft >= 0 ? apronSizeLeft : 0;
  const apronSizeRightLimited = apronSizeRight >= 0 ? apronSizeRight : 0;

  return currentWidth + apronSizeLeftLimited + apronSizeRightLimited;
}

export const requiredDistancesForApron = (aprons: any, spaces: any, currentDistances: number[] | null | undefined): number[] => {
  if (!currentDistances) { return [0]; }

  const apronSizeLeft = aprons.left && (aprons.left.outerWidth - spaces.left) || 0;

  let newDistances = JSON.parse(JSON.stringify(currentDistances));
  newDistances = newDistances.map((d: number) => d + apronSizeLeft);
  
  return newDistances;
}

export class ApronPartial extends React.Component<ApronProps, ApronState> {

  constructor(props: ApronProps) {
    super(props);
    this.state = {
      heightError: false,
    };
    this.handleChange = this.handleChange.bind(this);
    this.handleSlide = this.handleSlide.bind(this);
    this.handleSlideAfterChange = this.handleSlideAfterChange.bind(this);
    this.handleRadioChange = this.handleRadioChange.bind(this);
  }

  componentDidMount() {
    this.setState({
      apronHeight: this.apronHeight || (this.apronHeightDefaults && this.apronHeightDefaults.min)
    });
  }

  shouldComponentUpdate() {
    return true;
  }

  componentDidUpdate(prevProps: ApronProps, prevState: ApronState) {
    const {
      configuration,
      plateBuilder
    } = this.props;

    const prevConfiguration = prevProps.configuration;
    const prevPlateBuilder = prevProps.plateBuilder;

    if (
      (configuration.result && prevConfiguration.result) && 
      (configuration.result.updated_at !== prevConfiguration.result.updated_at)  
    ) {
      if (!this.apronHeight) { return; }
      this.setState({
        apronHeight: this.apronHeight || (this.apronHeightDefaults && this.apronHeightDefaults.min),
      });
    }
  }

  public render() {
    if (this.configuration && this.apronHeightDefaults && apronSpaceAvailable && simulatedAprons) {
      if ( this.props.plateBuilder) {
        // const targetElement = this.props.plateBuilder.elementsBuilder.buildTissueDispenser();
        const targetElement = this.props.plateBuilder.elementsBuilder.buildCutOut({"position":{"z":300},"troughIndex":0,"positionIndex":0,"isFirst":true}, {"key":"CUSTOM_ROUND","dimensions":{"depth":270,"width":270},"reqDimensions":{"apron":null,"depth":270,"width":270},"buffer":{"front":0,"left":15,"right":15},"shape":"ROUND","type":"WASTE","withRail":false,"withBasket":false,"withSleeve":false,"parts":[]} as any, 247.5);
        
        const distances =  this.props.plateBuilder.calc.spacePositions(targetElement, null, false); // roundDisposal.outerWidth
        const spacings =  this.props.plateBuilder.calc.spaceSizes(targetElement);
        // const spacings2 =  this.props.plateBuilder.calc.spaceSizes(roundDisposal);
        // console.log('have calcss');
        // console.log(distances, spacings, targetElement.outerWidth);

        // for (let index = 0; index < 2; index++) {
        //   const trough = this.props.plateBuilder.components.elements.filter(e => e.type ==='trough' && e.troughIndex === index)[0];
        //   console.log('RAAA');
        //   const spacing = this.props.plateBuilder.calc.spaceAroundElement(trough);
        //   console.log(trough, spacing);
        // }
        // console.log(this.props.plateBuilder.calc.hasConsoleOverlappingError());
        // console.log('calced stuff');


        // console.log('going to calc more stuff');
        // const trough = this.props.plateBuilder.components.elements.filter(e => e.type ==='trough' && e.troughIndex === 0)[0];
        // const tap = this.props.plateBuilder.elementsBuilder.buildTap();
        // const spacing = this.props.plateBuilder.calc.spaceAroundElement(trough);
        // console.log('spacing', spacing, tap.outerWidth);
        // console.log('calced more stuff');

      }
      
      const { heightError} = this.state;
      const apronHeight = this.state.apronHeight;

      let apronSizeLeft, apronSizeRight, hasSpace: boolean, spaceTotal, apronTotal, isTooSmall: boolean, widthLimitReached: boolean;
      let requiredWidth = 0;
      let requiredDistances: any[] = [];

      if (this.apronValue === 'without' && this.props.plateBuilder) {
        // console.log('before without checks');
        const aprons = simulatedAprons(this.props.plateBuilder);
        const spaces = apronSpaceAvailable(this.props.plateBuilder);

        apronSizeLeft = aprons.left && aprons.left.outerWidth || 0;
        apronSizeRight = aprons.right && aprons.right.outerWidth || 0;

        // Is there space for apron...
        hasSpace = hasSpaceForApron(aprons, spaces); // TODO pass in aprons and spaces
        const plateWidth = this.configuration && this.configuration.washBasinDimension && this.configuration.washBasinDimension.plateWidth;
        requiredWidth = requiredWidthForApron(aprons, spaces, plateWidth);
        const distances = this.configuration && this.configuration.washBasinDimension && this.configuration.washBasinDimension.distances;
        requiredDistances = requiredDistancesForApron(aprons, spaces, distances);
  
        // Do we have to increase width for apron...
        apronTotal = apronSizeLeft + apronSizeRight;
        spaceTotal = spaces.left + spaces.right; // Its looking at all of space on left and right, and seeing if its bigger than apron... the issue is I hardcoded the space positions now
        isTooSmall = spaceTotal < apronTotal; // We increase space by 1, this is like a really hacky fix because we round up the distances

        // console.log('have without checks', hasSpace, isTooSmall, spaces, apronTotal, requiredWidth, requiredDistances);

        const widthLimit = this.configuration.defaults.washBasinDimension.plateWidth.values.max;
        const widthCurrent = this.configuration.washBasinDimension && this.configuration.washBasinDimension.plateWidth || 0;
        widthLimitReached = widthCurrent + apronTotal > widthLimit;
      }

      return (
        <div className="partial-apron position-relative">
          {isConfigurationEditable() && (
            <WarningsTooltip itemKey="apron" />
          )}
          <ConsolesOverlapChoice id="apronOverlap" />

          {heightError && (
            <span className="info-icon mt-5" id="UncontrolledTooltipExample">
              <FontAwesomeIcon icon={faInfo} />
              <UncontrolledTooltip placement="right" target="UncontrolledTooltipExample">
                <FormattedMessage {...MESSAGES.apronHeightError} />
              </UncontrolledTooltip>
            </span>
          )}
          <div className="row">
            <div className="col-12">

              <div className="form-group">
                {this.apronDefaults && this.apronDefaults.allValues && this.apronDefaults.allValues.map((value, index) => {
                  const key = `apron_radio_key_${index}`;
                  const selected = this.apronValue && this.apronValue === value ? true : false;
                  const allowed = (_includes(this.apronDefaults && this.apronDefaults.values, value));
                  const disabled = (value === 'with' && this.apronValue === 'without' && !hasSpace) || !allowed;

                  return (
                    <div key={key} className="position-relative">
                      <div className="col-12">
                        <label className={classNames({disabled: this.disabled || disabled})}>
                          <span>{value === 'without' && formatMessage(MESSAGES.apronLabelWithout)}</span>
                          <span>{value === 'with' && formatMessage(MESSAGES.apronLabelWith)}</span>
                          <input 
                            type="radio" name="apron" value={value}
                            checked={selected} 
                            onChange={this.handleRadioChange}
                            disabled={this.disabled || disabled}
                          />
                          <span className="checkmark"/>
                        </label>
                        {allowed && value === 'without' && this.apronValue === 'with' && this.restorablePlateDimensions && (
                          <span className="info-icon" id="UncontrolledTooltipExample">
                            <FontAwesomeIcon icon={faInfo} />
                            <UncontrolledTooltip placement="right" target="UncontrolledTooltipExample">
                              <FormattedMessage {...MESSAGES.apronForcedWidthIncrease} />
                            </UncontrolledTooltip>
                          </span>
                        )}
                        {allowed && value === 'with' && this.apronValue === 'without' && !hasSpace && (
                          <>
                            {isTooSmall ? (
                              <><PlateDimensionsRequired id="apron" requiredWidth={requiredWidth} requiredDistances={requiredDistances} widthLimitReached={widthLimitReached}/></>
                            ) : (
                              <><PlateDimensionsChoice id="apron" requiredWidth={requiredWidth} requiredDistances={requiredDistances} movedDistances={this.movedDistances} widthLimitReached={widthLimitReached}/></>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {this.apronValue && this.apronValue === 'with' && this.apronHeight !== undefined && (
                <div className="partial-apron-height">
                  <div className="form-group">
                    <label className="small mb-2 mt-2 d-block">
                      <FormattedMessage {...MESSAGES.apronHeight} />
                    </label>
                    <DebounceInput
                      className="form-control"
                      name="apronHeight"
                      type="number"
                      value={apronHeight ? apronHeight : 0}
                      min={this.apronHeightDefaults.min}
                      max={this.apronHeightDefaults.max}
                      onChange={this.handleChange}
                      disabled={this.disabled}
                      debounceTimeout={500}
                    />
                  </div>

                  <div className="form-group ml">
                    <div className="row mt-4">
                      <div className="col-3 col-md-2 text-left">
                        {this.apronHeightDefaults.min}
                      </div>
                      <div className={this.apronHeightDefaults.min === 150 ? 'col-6 col-md-8 p-0 pt-1 disable' : 'col-6 col-md-8 p-0 pt-1'}>
                        <Slider
                          disabled={this.disabled}
                          marks={{
                            30: '',
                            60: '',
                            150: ''
                          }}
                          min={this.apronHeightDefaults.min}
                          max={this.apronHeightDefaults.max}
                          onChange={this.handleSlide}
                          onAfterChange={this.handleSlideAfterChange}
                          value={apronHeight ? apronHeight : 0}
                        />
                      </div>
                      <div className="col-3 col-md-2 text-right">
                        {this.apronHeightDefaults.max}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }
    return null;
  }

  private get disabled(): boolean {
    return !isConfigurationEditable();
  }

  private get apronDefaults(): ApronBackSplashDefaultModel['apron'] | undefined {
    return this.defaults && this.defaults.apron;
  }

  private get revertedWidth(): number {
    if (!this.configuration || !this.configuration.washBasinDimension || !this.props.plateBuilder) { return 0; }
    const aprons = simulatedAprons(this.props.plateBuilder);
    const apronSizeLeft = aprons.left && aprons.left.outerWidth || 0;
    const apronSizeRight = aprons.right && aprons.right.outerWidth || 0;

    return this.configuration.washBasinDimension.plateWidth - apronSizeLeft - apronSizeRight;
  }

  private get revertedDistances(): number[] {
    if (
      !this.configuration 
      || !this.configuration.washBasinDimension 
      || !this.configuration.washBasinDimension.distances) { return [0]; }

    if (!this.props.plateBuilder) { return this.configuration.washBasinDimension.distances }

    const aprons = simulatedAprons(this.props.plateBuilder);
    const apronSizeLeft = aprons.left && (aprons.left.outerWidth) || 0;

    let newDistances = JSON.parse(JSON.stringify(this.configuration.washBasinDimension.distances));
    newDistances = newDistances.map((d: number) => d - apronSizeLeft);
    
    return newDistances;
  }

  private get movedDistances(): number[] {
    if (
      !this.configuration 
      || !this.configuration.washBasinDimension 
      || !this.configuration.washBasinDimension.distances 
      || !this.props.plateBuilder) { return [0]; }
    
    
    const spaces = apronSpaceAvailable(this.props.plateBuilder);
    const aprons = simulatedAprons(this.props.plateBuilder);
    const apronSizeLeft = aprons.left && (aprons.left.outerWidth) || 0;
    const apronSizeRight = aprons.right && (aprons.right.outerWidth) || 0;

    let newDistances = JSON.parse(JSON.stringify(this.configuration.washBasinDimension.distances));

    if (spaces.left < apronSizeLeft) {
      const spaceRequired = apronSizeLeft - spaces.left;
      newDistances = newDistances.map((d: number) => d + spaceRequired);
    }
    if (spaces.right < apronSizeRight) {
      const spaceRequired = apronSizeRight - spaces.right;
      newDistances = newDistances.map((d: number) => d - spaceRequired);
    }
    
    return newDistances;
  }
  
  private get apronValue(): ApronBackSplashModel['apron'] | null | undefined {
    const { configuration } = this.props;
    return configuration && 
      configuration.result &&
      configuration.result.apronBackSplash &&
      configuration.result.apronBackSplash.apron;
  }
  
  private get restorablePlateDimensions(): ApronBackSplashModel['restorablePlateDimensions'] | null | undefined {
    const { configuration } = this.props;
    return configuration && 
      configuration.result &&
      configuration.result.apronBackSplash &&
      configuration.result.apronBackSplash.restorablePlateDimensions;
  }

  private get configuration(): ConfigurationModel | undefined {
    return this.props.configuration.result;
  }

  private get defaults(): ApronBackSplashDefaultModel | undefined {
    const {
      configuration,
    } = this.props;
    if (!configuration.result) {
      return;
    }
    return configuration.result.defaults.apronBackSplash;
  }

  private getApronHeight(): ApronBackSplashModel['apronHeight'] | undefined {
    const { configuration } = this.props;
    return configuration && 
      configuration.result &&
      configuration.result.apronBackSplash &&
      configuration.result.apronBackSplash.apronHeight ? configuration.result.apronBackSplash.apronHeight : null;
  }

  private get apronHeight(): ApronBackSplashModel['apronHeight'] | undefined {
    return this.getApronHeight();
  }

  private get apronHeightDefaults(): MinMaxModel | undefined {
    return this.defaults && this.defaults.apronHeight &&
      this.defaults.apronHeight.values;
  }

  private dispatchConfiguration(props: {apron?: ApronBackSplashModel['apron'] | null, apronHeight?: ApronBackSplashModel['apronHeight']}) {
    if (this.disabled) { return; }

    const data: ApronBackSplashModel = (this.configuration as ConfigurationModel).apronBackSplash as ApronBackSplashModel;
    if (props.apron) { 
      data.apron = props.apron; 
    }
    if (props.apronHeight) { 
      data.apronHeight = props.apronHeight; 
    }

    const washBasinDimension: any = (this.configuration as ConfigurationModel).washBasinDimension as WashBasinDimension;
    if (props.apron === 'without' && data.restorablePlateDimensions) {
      washBasinDimension.distances = data.restorablePlateDimensions.distances;
      washBasinDimension.plateWidth = data.restorablePlateDimensions.plateWidth;
      data.restorablePlateDimensions = null;
    }

    this.props.dispatch(requestAPIActions.load(
      'configuration', {
        url: `/Configurations/${this.configuration && this.configuration.id}`,
        options: {
          method: 'PATCH',
          data: {
            apronBackSplash: data,
            washBasinDimension
          }
        },
      },
      true,
    ));
  }

  private handleChange(event: React.SyntheticEvent<HTMLInputElement>): void {
    const target = event.target as HTMLInputElement;
    const value = Math.round(Number(target.value));
    const isValid = this.apronHeightDefaults ? this.validateMinMax(value, this.apronHeightDefaults) : false;

    this.setState({
      apronHeight: isValid && this.state.apronHeight !== value ? value : this.state.apronHeight,
      heightError: !isValid,
    });

    this.dispatchConfiguration({
      apronHeight: value,
    });
  }

  private handleRadioChange(event: React.SyntheticEvent<HTMLInputElement>) {
    const target = event.target as HTMLInputElement;
    const value = target.value as WITH_WITHOUT;
    // Dont know why defaults are not passing in correct apron height... tired, will try fix in future
    let apronHeight: ApronBackSplashModel['apronHeight'] = 60; // this.apronHeight || (this.apronHeightDefaults && this.apronHeightDefaults.min);
    if (value === 'without') {
      apronHeight = null;
    }
    this.setState({
      apronHeight,
    });
    this.dispatchConfiguration({
      apron: value,
      apronHeight,
    });
  }

  private handleSlide(value: number) {
    value = Math.round(value);
    const isValid = this.apronHeightDefaults ? this.validateMinMax(value, this.apronHeightDefaults) : false;

    this.setState({
      apronHeight: value,
      heightError: !isValid,
    });
  }

  private handleSlideAfterChange(value: number) {
    value = Math.round(value);
    this.setState({
      apronHeight: this.state.apronHeight !== value ? value : this.state.apronHeight,
    });

    this.dispatchConfiguration({
      apronHeight: value,
    });
  }

  private validateMinMax(value: number, values: MinMaxModel): boolean {
    if (value > values.max || value < values.min) {
      return false;
    }
    return true;
  }
}

export const Apron = connect((state: GlobalPrivateState) => ({
  plateBuilder: state.plateBuilder,
  configuration: state.configuration,
}))(ApronPartial);

export default Apron;